"use client"

// components/tracker/track-drug-picker.tsx
//
// The add-drug picker block — heading + subheading, the black chat picker
// panel, suggestion chips, and an optional below-card slot. Extracted from
// TrackDrugModal so the same markup can render either inside the modal (on the
// dashboard) or inline on a page, guaranteeing they stay identical.

import type { ReactNode } from "react"

import { PublicPipelinePicker } from "@/components/public-tools/public-pipeline-picker"
import { TrackerSuggestedDrugs } from "@/components/public-tools/tracker-suggested-drugs"
import publicStyles from "@/components/public-tools/public-trials-pipeline-timeline.module.css"
import timelineStyles from "@/components/styles/trials-pipeline-timeline.module.css"
import { MAX_WATCHLIST_DRUGS } from "@/lib/pipeline-watch/track-drug"

export type TrackDrugPickerProps = {
  trackedDrugs: string[]
  onSearchSelect: (displayTerm: string) => void | Promise<void>
  /** Let Enter submit raw typed text (free-text search surfaces). */
  submitOnEnter?: boolean
  /** Per-keystroke filter text (live-search surfaces). Caller debounces. */
  onFilterChange?: (text: string) => void
  /** Seed the box once post-mount (deep links) — see PublicPipelinePicker. */
  seedText?: string
  loadingGenerics?: string[]
  statusMessage?: string | null
  statusClassName?: string
  heading?: string
  subheading?: string
  belowSlot?: ReactNode
  titleId?: string
  panelZIndex?: number
  /**
   * Render the dark card (black panel + white interior text) deterministically,
   * dropping the `...LightEmpty` overrides. The empty-state classes leave the
   * card background to a `var(--panel)` vs `transparent` cascade race whose
   * winner depends on Next.js CSS chunk order per page — fine inside the modal
   * (where it lands dark), but on a fresh marketing page the overrides can win
   * and wash the panel to white. `dark` forces the black look every time.
   */
  dark?: boolean
  /**
   * Optional overlay rendered as a sibling of the black card, inside a
   * relative stage sized to the card — used by the closing CTA to flank
   * alert cards over the picker’s edges without covering the heading.
   */
  cardOverlay?: ReactNode
  /**
   * Stretch the card to the parent width instead of the centered 75%
   * marketing layout (Hero237 left column, etc.).
   */
  stretch?: boolean
  /**
   * Black brand search bar + light suggestion ink (explore). Pair with
   * `flush` when nesting inside an existing card (e.g. Hero237) so we don't
   * stack a second plate.
   */
  raised?: boolean
  /**
   * Skip card / stage / ctrl chrome — render search + suggestions directly
   * on the parent surface (Hero237). Avoids timeline `.card` / `.ctrl`
   * padding winning the CSS cascade over `cardLightEmpty`.
   */
  flush?: boolean
  /** Include the chat search trigger. Default true. */
  showSearch?: boolean
  /** Override the search field's placeholder — see PublicPipelinePicker. */
  chatPlaceholder?: string
  /** Include suggested-drug tabs + list. Default true. */
  showSuggestions?: boolean
}

export function TrackDrugPicker({
  trackedDrugs,
  onSearchSelect,
  loadingGenerics = [],
  statusMessage = null,
  statusClassName,
  heading = "Which drug's trials do you want to track?",
  subheading = "We check every one of its trials daily and tell you what moved.",
  belowSlot,
  titleId,
  panelZIndex = 310,
  dark = false,
  cardOverlay,
  stretch = false,
  raised = false,
  flush = false,
  showSearch = true,
  showSuggestions = true,
  chatPlaceholder,
  submitOnEnter = false,
  onFilterChange,
  seedText,
}: TrackDrugPickerProps) {
  const rootClassName = [
    timelineStyles.root,
    publicStyles.root,
    publicStyles.rootCentered,
    !dark ? publicStyles.rootLightEmpty : "",
    raised && !dark ? publicStyles.rootRaisedInk : "",
  ]
    .filter(Boolean)
    .join(" ")

  const search = showSearch ? (
    <PublicPipelinePicker
      selectedDrugs={[]}
      blockedGenerics={trackedDrugs}
      parties={[]}
      maxSelection={MAX_WATCHLIST_DRUGS}
      onSelectionChange={() => {}}
      onSearchSelect={onSearchSelect}
      submitOnEnter={submitOnEnter}
      onFilterChange={onFilterChange}
      seedFilter={seedText}
      chatPlaceholder={chatPlaceholder}
      loadingGenerics={loadingGenerics}
      triggerVariant="chat"
      panelZIndex={panelZIndex}
      surface={raised ? "raised" : "default"}
    />
  ) : null

  const suggestions = showSuggestions ? (
    <TrackerSuggestedDrugs
      onSearchSelect={onSearchSelect}
      raised={raised}
      flushTop={!showSearch}
    />
  ) : null

  const header = heading ? (
    <header className={publicStyles.ctrlHeaderAboveCard}>
      <h2 id={titleId} className={publicStyles.ctrlHeadingAboveCard}>
        {heading}
      </h2>
      {subheading ? (
        <p className={publicStyles.ctrlSubheadingAboveCard}>{subheading}</p>
      ) : null}
    </header>
  ) : null

  const footer = (
    <>
      {belowSlot ? (
        <div className={publicStyles.belowCardSlot}>{belowSlot}</div>
      ) : null}
      {statusMessage ? (
        <p className={statusClassName} role="status">
          {statusMessage}
        </p>
      ) : null}
    </>
  )

  // Flush: no card/stage/ctrl wrappers — just the search field (and/or
  // suggestions) on the parent surface.
  if (flush) {
    return (
      <div className={`${rootClassName} ${publicStyles.flushRoot}`}>
        {header}
        {search}
        {suggestions}
        {footer}
      </div>
    )
  }

  const cardClassName = [
    timelineStyles.card,
    publicStyles.card,
    stretch ? publicStyles.cardStretch : publicStyles.cardCentered,
    raised ? publicStyles.cardRaised : !dark ? publicStyles.cardLightEmpty : "",
  ]
    .filter(Boolean)
    .join(" ")
  const stageClassName = stretch
    ? `${publicStyles.cardStage} ${publicStyles.cardStageStretch}`
    : publicStyles.cardStage

  return (
    <div className={rootClassName}>
      {header}

      <div className={stageClassName}>
        {cardOverlay}
        <div className={cardClassName}>
          {showSearch ? (
            <div
              className={`${timelineStyles.ctrl} ${publicStyles.ctrl} ${publicStyles.ctrlCentered}`}
            >
              {search}
            </div>
          ) : null}
          {suggestions}
        </div>
      </div>

      {footer}
    </div>
  )
}
